import type { ProductMessage } from "./ProductMessages";

/**
 * Structural shape of one `product_messages` row. Declared here rather than
 * imported from the catalog domain: this composite is client-side and must
 * stay free of server-only domain code, and a structural type keeps
 * `CatalogProductMessage[]` assignable without the coupling.
 */
export interface ProductMessageSource {
	title: string;
	code?: string;
	icon?: string;
	link?: { title: string; href: string };
	startDate?: string;
	endDate?: string;
}

/**
 * The message rows a shopper should see right now, in authored order — only
 * the first two render, so the order the author chose decides what shows. The
 * projection passes a date window through unevaluated, because nothing
 * re-projects a product when a window opens or closes, so it resolves here.
 */
export function selectProductMessages(
	productMessages: ProductMessageSource[] | undefined,
	now: number = Date.now()
): ProductMessage[] {
	return (productMessages ?? [])
		.filter((message) => isWithinWindow(message, now))
		.map((message) => ({
			text: message.title,
			icon: message.icon,
			link: toLink(message.link)
		}));
}

function isWithinWindow(message: ProductMessageSource, now: number): boolean {
	if (message.startDate && Date.parse(message.startDate) > now) return false;
	if (message.endDate && Date.parse(message.endDate) < now) return false;
	return true;
}

// The CMS stores an unset Link as an empty object, so an href is what counts.
function toLink(
	link: ProductMessageSource["link"]
): ProductMessage["link"] | undefined {
	if (!link?.href) return undefined;
	return { label: link.title || "See Details", href: link.href };
}
